import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import moment from "moment";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import axiosService from "../../../core/services/axios-service";
import { ActionDropdown } from "../../../components/designs/action-dropdown";
import { MetricsCard } from "../../../components/designs/matrics-card";
import SideDrawer from "../../../components/designs/side-drawer";
import {
  Building2,
  Users,
  Plus,
  Edit,
  Eye,
  BookOpen,
  UserPlus,
  BarChart3,
  ExternalLink,
} from "lucide-react";
import { ListingHeader } from "../../../components/listing-header";
import { SearchAndFiltersListing } from "../../../components/search-filter-listing";
import { RescueRnTable } from "../../../components/designs/rescueRn-table";
import { getInitials } from "../../../core/services/global-functions";
import { organisationActionType } from "../../../core/dictionary/global-enums";
import AddEditOrganization from "./components/add-edit-organization";
import ViewOrganization from "./components/view-organization";
import AddEditCoachTeamMember from "../../../components/add-edit-coach-team-member";
import AddBusinessAdminUser from "./components/add-organization-admin-user";
export default function OrganizationsPage() {
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(1);
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("organizationName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [isSidedrawerOpen, setIsSidedrawerOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  // Define actions for the ActionDropdown

  const actions = [
    {
      id: "edit",
      label: "Edit organization",
      icon: Edit,
      value: organisationActionType.edit,
    },
    {
      id: "view",
      label: "View organization",
      icon: Eye,
      value: organisationActionType.view,
    },
    {
      id: "assign",
      label: "Add Admin User",
      icon: BookOpen,
      value: organisationActionType.addOrganizationAdminUser,
    },
    {
      id: "coach",
      label: "Add coach",
      icon: UserPlus,
      value: organisationActionType.addCoach,
    },
    {
      id: "team",
      label: "Add Team members",
      icon: Users,
      value: organisationActionType.addTeam,
    },
    {
      id: "dashboard",
      label: "organization performance dashboard",
      icon: BarChart3,
      value: organisationActionType.performanceDashboard,
    },
  ];
  const columns = [
    {
      name: "Organization",
      selector: (row) => row.organizationName,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <div className="organization-avatar me-3">
            {getInitials(row.organizationName)}
          </div>
          <Link
            to={`/admin/organizations/${row.organization_id}`}
            className="fw-medium" style={{ color: "var(--brand-navy)" }}
            title="Open Organization"
          >
              {row.organizationName}
              <ExternalLink size={14} className="ms-1" />
          </Link>
        </div>
      ),
      width: "250px",
    },
    {
      name: "Address",
      selector: (row) => row.organizationAddress,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.organizationAddress}
        </span>
      ),
    },
    {
      name: "Contact Number",
      selector: (row) => row.organizationContactNumber,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.organizationContactNumber}
        </span>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.organizationEmail,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.organizationEmail}
        </span>
      ),
    },
    {
      name: "Created",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.createdAt
            ? moment(row.createdAt).format("Do MMM YYYY, h:mm A")
            : ""}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionDropdown
          rowData={row}
          actions={actions}
          actionDropClicked={(data) => actionDropClicked(data)}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
  ];

  const fetchData = async (
    page = 1,
    size = 5,
    sort = "organizationName",
    order = "asc",
    search = ""
  ) => {
    setLoading(true);
    const res = await axiosService.get(ApiRoutes.ORGANIZATION_LISTING, {
      params: {
        perPage: size,
        currentPage: page,
        sortBy: sort,
        sortOrder: order,
        searchStr: search || "",
      },
    });

    setData(res.data.data || []);
    setTotalRows(res.data.total || 1);
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400); // 400ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchData(currentPage, perPage, sortBy, sortOrder, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, sortBy, sortOrder, debouncedSearch]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };
  const handleSort = (column, sortDirection) => {
    setSortBy(column.sortField || "organizationName");
    setSortOrder(sortDirection);
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const addOrganization = () => {
    // Logic to add organization
    setSelectedOrganization(null);
    setActionType(organisationActionType.add);
    setTimeout(() => {
      setIsSidedrawerOpen(true); // Open the sidedrawer
    }, 100);
  };

  const commonActionOrganization = (data) => {
    setSelectedOrganization(data);
    setTimeout(() => {
      setIsSidedrawerOpen(true);
    }, 100);
  };
  const actionDropClicked = (data) => {
    if (
      data.action === organisationActionType.edit ||
      data.action === organisationActionType.view ||
      data.action === organisationActionType.addCoach ||
      data.action === organisationActionType.addTeam ||
      data.action === organisationActionType.addOrganizationAdminUser
    ) {
      // Edit and View is Managed by this Condition only
      setActionType(data.action);
      commonActionOrganization(data.rowData);
    } else if (data.action === "delete") {
      deleteOrganization(data.rowData);
    }
  };

  const deleteOrganization = (orgId) => {
    // Logic to delete organization
    console.log("Delete organization with ID:", orgId);
  };
  const getNewOrganizationsCount = (organizations) => {
    // Get the first and last day of the previous calendar month
    const startOfLastMonth = moment().subtract(1, "months").startOf("month");
    const endOfLastMonth = moment().subtract(1, "months").endOf("month");
    return organizations.filter((org) =>
      moment(org.createdAt).isBetween(
        startOfLastMonth,
        endOfLastMonth,
        null,
        "[]"
      )
    ).length;
  };
  return (
    <>
      <ListingHeader
        title={"Organizations"}
        breadCrumb={[
          { label: "Dashboard", href: "/admin/dashboard" },
          { label: "Organizations", href: "/admin/organizations" },
        ]}
        actions={[
          {
            label: "Add Organization",
            className: "btn btn-rescuern-primary",
            icon: Plus,
            onClick: () => {
              addOrganization();
            },
          },
        ]}
      />

      <div className="flex-grow-1 p-4">
        <div className="container-fluid p-0">
          <div className="row g-4 mb-4">
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Total Organizations"
                value={loading ? "--" : totalRows}
                icon={Building2}
              />
            </div>
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="New This Month"
                value={
                  loading ? "--" : totalRows - getNewOrganizationsCount(data)
                }
                icon={Users}
              />
            </div>
          </div>
          <div className="mb-4">
            <SearchAndFiltersListing
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
            />
          </div>
          <RescueRnTable
            data={data}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            loadingTableText="Loading organizations..."
            handleSort={handleSort}
            onChangePage={(page) => handlePageChange(page)}
            handlePerRowsChange={(newPerPage) => handlePerRowsChange(newPerPage)}
            perPage={perPage}
          />
        </div>
      </div>
      {/* End of the above code */}
      {/* Below is the sidedrawer component */}
      <SideDrawer
        title={
          actionType === organisationActionType.add
            ? "Add Organization"
            : actionType === organisationActionType.edit
            ? "Edit Organization"
            : actionType === organisationActionType.view
            ? "View Organization"
            : actionType === organisationActionType.assign
            ? "Assign Courses"
            : actionType === organisationActionType.addCoach
            ? "Add Coach"
            : actionType === organisationActionType.addTeam
            ? "Add Team Members"
            : actionType === organisationActionType.dashboard
            ? "Organization Performance Dashboard"
            : actionType === organisationActionType.addOrganizationAdminUser
            ? "Add Business Admin User"
            : ""
        }
        isOpen={isSidedrawerOpen}
        onClose={() => setIsSidedrawerOpen(false)}
        closeOnOverlay={false}
      >
        {/* Below code is for the Add and Edit Organization */}
        {actionType === organisationActionType.add ||
        actionType === organisationActionType.edit ? (
          <AddEditOrganization
            type={actionType}
            organizationId={
              selectedOrganization ? selectedOrganization.organization_id : null
            }
            onClose={() => setIsSidedrawerOpen(false)}
            onSuccess={(res) => {
              setIsSidedrawerOpen(false);
              fetchData(
                currentPage,
                perPage,
                sortBy,
                sortOrder,
                debouncedSearch
              );
            }}
          ></AddEditOrganization>
        ) : null}
        {/* End of the above code */}
        {/* Below code is for the View Organization */}
        {actionType === organisationActionType.view ? (
          <ViewOrganization
            organizationId={
              selectedOrganization ? selectedOrganization.organization_id : null
            }
          />
        ) : null}
        {/* End of the above code */}
        {/* Below code is for the Adding the Coach and Team Members */}
        {actionType === organisationActionType.addCoach ||
        actionType === organisationActionType.addTeam ? (
          <AddEditCoachTeamMember
            type={actionType}
            organizationId={
              selectedOrganization ? selectedOrganization.organization_id : null
            }
            onClose={() => setIsSidedrawerOpen(false)}
            onSuccess={(res) => {
              setIsSidedrawerOpen(false);
              fetchData(
                currentPage,
                perPage,
                sortBy,
                sortOrder,
                debouncedSearch
              );
            }}
          ></AddEditCoachTeamMember>
        ) : null}
        {/* End of the above code */}
        {/* Below is the code for Adding the Organization Admin User */}
        {actionType === organisationActionType.addOrganizationAdminUser ? (
          <AddBusinessAdminUser
            organizationId={
              selectedOrganization ? selectedOrganization.organization_id : null
            }
            onClose={() => setIsSidedrawerOpen(false)}
            onSuccess={(res) => {
              setIsSidedrawerOpen(false);
              fetchData(
                currentPage,
                perPage,
                sortBy,
                sortOrder,
                debouncedSearch
              );
            }}
          ></AddBusinessAdminUser>
        ) : null}

        {/* End of the above code */}
      </SideDrawer>
      {/* End of the above code */}
    </>
  );
}
